import React, { useEffect, useCallback, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../src/app/authConfig';
import { getUserProfile } from './app/Services/graphService';
import axios from 'axios';

interface AuthWrapperProps {
    children: React.ReactNode;
}

const AUTHORITY = 'https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6';

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const [userMail, setUserMail] = useState('');
    const [isReady, setIsReady] = useState(false);

    const requestProfileData = useCallback(async () => {
        if (!accounts || accounts.length === 0) return; // Guard
        
        try {
            const profile = await getUserProfile(instance, AUTHORITY, true);
            setUserMail(profile.mail?.toLowerCase() || '');
        } catch (error) {
            console.error("Failed to get user profile, redirecting to login:", error);
            instance.loginRedirect(loginRequest);
        }
    }, [instance, accounts]);

    const conditionCheck = useCallback(async () => {
        if (userMail !== '') {
            try {
                // Ensure we have an account before calling acquireTokenSilent
                const currentAccounts = instance.getAllAccounts();
                if (currentAccounts.length === 0) {
                    console.error("AuthWrapper: No accounts available");
                    return;
                }

                const account = instance.getActiveAccount() || currentAccounts[0];
                
                const res = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: account,
                });
            } catch (error: any) {
                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    navigate('/no-access');
                } else {
                    console.error("An error occurred:", error);
                    navigate('/technical-error');
                }
            }
        }
    }, [instance, userMail, navigate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await requestProfileData();
                await conditionCheck();
            } catch (error) {
                navigate('/error');
            }
        };

        const checkToken = async () => {
            try {
                // Ensure we have an account before calling acquireTokenSilent
                const currentAccounts = instance.getAllAccounts();
                if (currentAccounts.length === 0) {
                    console.error("AuthWrapper checkToken: No accounts available, redirecting to login");
                    instance.loginRedirect(loginRequest);
                    return;
                }

                const account = instance.getActiveAccount() || currentAccounts[0];
                
                await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: account,
                });
            } catch (error) {
                console.error("Token is expired or not available, redirecting to SSO login");
                instance.loginRedirect(loginRequest);
            }
        };

        if (inProgress === InteractionStatus.None) {
            if (!isAuthenticated) {
                instance.loginRedirect({ scopes: ["User.Read", "User.Read.All"] });
            } else {
                // User is authenticated, check token and fetch data
                const initAuth = async () => {
                    await checkToken();
                    await fetchData();
                    setIsReady(true); // Mark as ready after auth is complete
                };
                initAuth();
            }
        }
    }, [instance, isAuthenticated, navigate, inProgress, requestProfileData, userMail, conditionCheck]);

    // Don't render children until authentication is complete and ready
    if (inProgress !== InteractionStatus.None) {
        return <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
        }}>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
        }}>Redirecting to login...</div>;
    }

    if (!isReady) {
        return <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
        }}>Authenticating...</div>;
    }

    return <>{children}</>;
};

export default AuthWrapper;